---
title: Agent MCP
lede: The agent opens your pages, sends your saved calls, runs its projects and drafts its comments through Airlock's own tools, and every move is recorded.
order: 22
part: Check the result
---

Airlock gives the agent tools to open this session's pages, click and type in them, take screenshots, read what they logged, and send the project's saved API calls. An agent that changed the checkout page can go and look at it. The same tools let it start, stop and restart the session's projects and read their logs, open a public URL onto one, leave notes on the code, and draft comments for the session's pull request.

Preview is on by default: **Settings › All agents › Preview › Let the agent drive Preview**. A workspace can decide for itself in **Workspace settings › Agent**.

### The trail

![The agent trail](media/dock-agent-trail.webp)

The dock's **Agent MCP** tab (`⌥⌘J` shows the dock; the status bar's **Agent MCP** button opens the tab) lists everything the agent did through Airlock's tools. A band counts the actions, the failures and the screenshots; each row gives the tool, its target, the short answer and the time. A refused call is in red, with its reason.

Pick a row to see what it answered beside the list: the screenshot, the request and response, the console lines. Drag the divider to resize, or open the view full screen. The arrow keys walk the rows; until you pick one, the view follows the newest.

### What lands on the trail

| Row | What the agent did |
|---|---|
| Pages and calls | Opened, clicked, typed, screenshotted, read the console, sent a saved call |
| Saved logins | Signed a page in with a login from the vault. The row says a login was used, never the password |
| Projects | Started, stopped or restarted a project, or read its logs |
| Public URLs | Opened or closed a [tunnel](/tunnels) onto a project's port |
| Notes on the code | Left a finding on the lines it is about, in the review |
| Pull request comments | Drafted a comment for the session's [pull request](/pull-requests) |
| Probes | Set a [live probe](/live-probes), with its count of hits |
| Targets | Moved the session to another [target](/targets), or asked to |

A comment the agent drafts is never posted by itself: it waits in **Changes** for you to post or delete it.

### What it can reach

Only the ports its own session publishes, the same as the [browser](/browser#what-it-can-reach). It cannot reach a port of your computer or another session.

### Saved logins

**…including saved logins** (in a workspace: **…using this workspace's saved logins**) lets the agent ask this computer to sign a page in with a login from the [vault](/vault). This computer types the password, so it never enters the sandbox.

The signed-in page is screenshotted back to the agent. If a workspace's logins reach real data, turn this off for that workspace.

### Live probes

The agent can also watch a line of a running project, or of the page it opened, without editing or restarting it. See [Live probes](/live-probes).

### Turning it off

Each switch can be turned off per workspace. Sessions run the same way without them; the agent just cannot see the result of its work.
